import { AutoUser } from './auto-user';
import { CoordinatorService } from './coordinator.service';
import { ProjectMember } from './project-member';
import { INIT_AUTOUSER, ScriptAutoUser } from './script-auto-user';
import { ScriptHitset } from './script-hitset';
import { ScriptOutput } from './script-output';
import { ScriptRatings } from './script-ratings';
import { ScriptTags } from './script-tags';
import { ScriptHitsetInterface } from './script-types';

export class ScriptUser {
    #coords: CoordinatorService;
    #user?: ProjectMember;
    #autouser?: AutoUser;
    isLocalUser = false;

    constructor(coords: CoordinatorService, user?: ProjectMember) {
        this.#coords = coords;
        this.#user = user;
        this.isLocalUser = !this.#user;
    }

    adopt(
        xu: ScriptAutoUser | ScriptUser,
        {
            mode,
            ratingsMode,
            tagsMode,
            omitComments,
            omitRatings,
            omitTags,
            hitset,
        }: {
            mode?: 'copy' | 'move';
            ratingsMode?: 'replace' | 'if-empty';
            tagsMode?: 'replace' | 'if-empty' | 'merge';
            omitComments?: boolean;
            omitRatings?: string | string[] | ScriptRatings | ScriptRatings[];
            omitTags?: string | string[] | ScriptTags | ScriptTags[];
            hitset?: ScriptHitset | ScriptHitsetInterface;
        } = {},
    ) {
        if (this.#user && !this.#user.isCurrentUser) return;
        console.log('XU', xu);

        const importAU = xu instanceof ScriptUser ? xu.autouser() : xu;
        const p = this.#coords.project$.current!;
        !omitComments && p.adoptData(importAU.provideData('comment'));

        const omitBtSet = new Set<string>();
        this.#coords.binaryTree$.adoptOmitBtIdLs(omitBtSet, 'ratings', omitRatings);
        this.#coords.binaryTree$.adoptOmitBtIdLs(omitBtSet, 'tags', omitTags);

        const inputData = importAU.provideData('binary-tree');

        const params: any = {};
        ratingsMode && (params.ratingsMode = ratingsMode);
        tagsMode && (params.tagsMode = tagsMode);
        hitset &&
            (params.hitset = (hitset instanceof ScriptHitset ? hitset : hitset.hitset()).idSet);
        omitBtSet.size && (params.omitBtSet = omitBtSet);

        p.adoptData(inputData, params);

        if (mode === 'move') {
            if (!omitComments) xu.clear('comments');
            if (!omitRatings) xu.clear('ratings');
            if (!omitTags) xu.clear('tags');
            if (omitBtSet.size) {
                this.#coords.binaryTree$.list.forEach((bt) => {
                    if (!omitBtSet.has(bt.id)) {
                        this.clear(bt.id);
                    }
                });
            }
        }

        return this;
    }

    get name() {
        return this.#user?.user?.name || this.#coords.text('Author');
    }

    get id() {
        if (this.isLocalUser) return '_localUser';
        return this.#user?.id!;
    }

    log(prefix = 'USER') {
        console.log(`${prefix}: ${this.name}`);
        return this;
    }

    clear(arg: ScriptRatings | ScriptTags | string | 'ratings' | 'tags' | 'comments' | 'all') {
        if (!this.isLocalUser && !this.#user?.isCurrentUser) return this;
        if (!arg) return console.log('No argument provided for clear()');

        this.#coords.project$.current?.clearScriptCurrentUser(
            typeof arg === 'string' ? arg : arg.id,
        );
        return this;
    }

    autouser(arg1: string | INIT_AUTOUSER | boolean = '', arg2: INIT_AUTOUSER | boolean = false) {
        if (!arg1 && !this.#autouser) {
            this.#autouser = new AutoUser(this.#coords, { name: `${this.name}` });
        }

        let toProject = false;

        if (typeof arg1 !== 'string') {
            arg2 = arg1;
            arg1 = '';
        }

        if (typeof arg2 === 'boolean') {
            toProject = !!arg2;
            arg2 = {};
        } else {
            if (arg2.name) arg1 = arg2.name;
            toProject = !!arg2.toProject;
        }

        arg1 = arg1?.trim();
        let au = this.#autouser;
        if (!au) {
            this.#autouser = new AutoUser(this.#coords, {
                name: arg1 || `${this.name}`,
            });
            au = this.#autouser;
        }

        const commentLs =
            (this.#user && this.#user.commentLs) || this.#coords.comment$.currentUserCommentLs;

        const btMap = (this.#user && this.#user.btMap) || this.#coords.binaryTree$.btMap;

        const sau = au.scriptClass;

        sau.load({ commentLs, btMap }, arg2);
        if (toProject) {
            sau.toProject();
        }
        return sau;
    }

    async export() {
        console.log('EXPORT', this);
        const su = new ScriptOutput(this.#coords);
        await su.save();
    }

    get isCurrent() {
        return !!this.#user?.isCurrentUser;
    }

    transferDialog(isBulk = false) {
        this.#coords.display$.dialog$.next({
            action: 'autouser-transfer',
            user: this,
            isBulk,
        });
    }
}
